import { Paper, Text, Table, Badge, Progress, Group, Stack, Flex } from "@mantine/core";
import { IconTarget } from "@tabler/icons-react";

/**
 * Devuelve color según el porcentaje de cumplimiento
 */
const getProgressColor = (percent) => {
    if (percent >= 100) return "green";
    if (percent >= 70) return "yellow";
    if (percent >= 40) return "orange";
    return "red";
};

/**
 * Seguimiento de objetivos de inscripción por unidad académica.
 * Muestra: nombre, tipo, objetivo, inscriptos equivalentes al 100%, progreso, pagos totales, revenue.
 */
export default function ObjectivesTracker({ objectives }) {
    if (!objectives || objectives.length === 0) {
        return (
            <Paper shadow="sm" radius="md" p="md">
                <Group gap="xs" mb="sm">
                    <IconTarget size={20} />
                    <Text fw={700}>Seguimiento de Objetivos</Text>
                </Group>
                <Text c="dimmed" size="sm">No hay datos de objetivos disponibles.</Text>
            </Paper>
        );
    }

    // Resumen global
    const totalTarget = objectives.reduce((sum, o) => sum + (o.inscriptionTarget || 0), 0);
    const totalEquivalent = objectives.reduce((sum, o) => sum + (o.equivalentAt100 || 0), 0);
    const globalProgress = totalTarget > 0 ? Math.round((totalEquivalent / totalTarget) * 1000) / 10 : 0;

    return (
        <Paper shadow="sm" radius="md" p="md">
            <Group gap="xs" mb="xs">
                <IconTarget size={20} />
                <Text fw={700}>Seguimiento de Objetivos</Text>
            </Group>

            {/* Resumen global */}
            <Flex gap="lg" mb="md" wrap="wrap">
                <Stack gap={0}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Objetivo global</Text>
                    <Text size="lg" fw={700}>
                        {totalEquivalent.toFixed(1)} / {totalTarget}
                        <Text span size="sm" c="dimmed" ml={4}>({globalProgress}%)</Text>
                    </Text>
                </Stack>
                <Stack gap={0}>
                    <Text size="xs" c="dimmed" tt="uppercase" fw={600}>Capacitaciones</Text>
                    <Text size="lg" fw={700}>{objectives.length}</Text>
                </Stack>
            </Flex>

            <Progress
                value={Math.min(globalProgress, 100)}
                color={getProgressColor(globalProgress)}
                size="lg"
                radius="md"
                mb="md"
            />

            {/* Tabla detallada */}
            <div style={{ overflowX: 'auto' }}>
                <Table striped highlightOnHover withTableBorder fontSize="sm">
                    <Table.Thead>
                        <Table.Tr>
                            <Table.Th>Capacitación</Table.Th>
                            <Table.Th>Tipo</Table.Th>
                            <Table.Th style={{ textAlign: 'center' }}>Objetivo</Table.Th>
                            <Table.Th style={{ textAlign: 'center' }}>Equiv. 100%</Table.Th>
                            <Table.Th style={{ textAlign: 'center' }}>Pagos</Table.Th>
                            <Table.Th style={{ width: 160 }}>Progreso</Table.Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {objectives.map((obj) => {
                            const color = getProgressColor(obj.progressPercent);
                            return (
                                <Table.Tr key={obj._id}>
                                    <Table.Td>
                                        <Stack gap={0}>
                                            <Text size="sm" fw={500} lineClamp={1}>
                                                {obj.auName}
                                            </Text>
                                            {obj.auInitialDate && (
                                                <Text size="xs" c="dimmed">{obj.auInitialDate}</Text>
                                            )}
                                        </Stack>
                                    </Table.Td>
                                    <Table.Td>
                                        <Badge
                                            variant="light"
                                            size="sm"
                                            color={
                                                obj.auType === 'Diplomado' ? 'blue' :
                                                obj.auType === 'Posgrado' ? 'violet' :
                                                obj.auType === 'Curso' ? 'teal' : 'gray'
                                            }
                                        >
                                            {obj.auType || '-'}
                                        </Badge>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm" fw={600}>{obj.inscriptionTarget}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm" fw={600}>{obj.equivalentAt100}</Text>
                                    </Table.Td>
                                    <Table.Td style={{ textAlign: 'center' }}>
                                        <Text size="sm">{obj.totalPayments}</Text>
                                    </Table.Td>
                                    <Table.Td>
                                        <Group gap={4} wrap="nowrap">
                                            <Progress
                                                value={Math.min(obj.progressPercent, 100)}
                                                color={color}
                                                size="md"
                                                radius="md"
                                                style={{ flex: 1 }}
                                            />
                                            <Badge
                                                color={color}
                                                variant="light"
                                                size="sm"
                                                w={55}
                                                styles={{ root: { textAlign: 'center' } }}
                                            >
                                                {obj.progressPercent}%
                                            </Badge>
                                        </Group>
                                    </Table.Td>
                                </Table.Tr>
                            );
                        })}
                    </Table.Tbody>
                </Table>
            </div>
        </Paper>
    );
}
